import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, SearchBar, searchMatch } from '../ui'
import { GameCard } from '../cards'
import { getNFLSlate, searchNFLPlayers, getNFLMarket } from '../api'
import { MatchupMatrix } from '../matchup'
import TopPlays from '../topplays'
import Cheatsheet from '../cheatsheet'
import MatchupsTab from './MatchupsTab'   // defense-vs-position — now inside Attack

// Games hub — the weekly slate (props game cards), the football-only Matchup Matrix
// (on-field grades), Top Plays (each game's best 4-5), Scorers (each game's most likely
// touchdown scorers), and the Cheatsheet (the whole week's matchups as sortable category
// tables — where to attack). Folded under one tab so the bottom nav stays at 6-7.
const MODES = [['slate', 'Games'], ['attack', 'Attack'], ['top', 'Top Plays']]

// Attack — the one "where to attack this week" home. Consolidates what used to be three
// scattered surfaces (the Cheatsheet, the Matchup grade matrix, and Players → Defense)
// into one place with sub-tabs, so a user isn't hunting the same question across tabs.
const ATTACK_SUBS = [['sheet', 'Cheat sheet'], ['grades', 'Grades'], ['defense', 'Defense']]
function AttackView({ season, week, onGame, onPlayer, pro, initialSub }) {
  const [sub, setSub] = useState(initialSub || 'sheet')
  return (
    <div>
      <div style={{ fontSize: 11, color: T.muted, marginBottom: 10, lineHeight: 1.4 }}>
        Where to attack this week — softest spots by category, each game's matchup grade, and defense vs. position.
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {ATTACK_SUBS.map(([id, label]) => {
          const on = id === sub
          return (
            <button key={id} onClick={() => setSub(id)} style={{ flex: 1, padding: '8px 0', borderRadius: 9,
              cursor: 'pointer', fontWeight: 800, fontSize: 12, border: `1px solid ${on ? T.green : T.border}`,
              background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{label}</button>
          )
        })}
      </div>
      {sub === 'sheet' && <Cheatsheet season={season} week={week} onGame={onGame} pro={pro} />}
      {sub === 'grades' && <MatchupMatrix season={season} week={week} />}
      {sub === 'defense' && <MatchupsTab season={season} week={week} onPlayer={onPlayer} />}
    </div>
  )
}

// Preferred order for the time-slot chips (only windows present this week render).
const WINDOW_ORDER = ['Thursday', 'Sun AM', 'Sun early', 'Sun late', 'Sun night', 'Monday', 'Friday', 'Saturday', 'Sunday']

// TD Board — the NFL analog of MLB's Homer Board: a collapsible, ranked overview of the
// slate by projected touchdowns (expected TD scorers = sum of every player's anytime-TD
// probability), each game showing its venue + most likely scorer. Reads the slate's
// proj_tds / td_leader — no extra fetch. Expected values only, no picks.
function TdBoard({ games }) {
  const [open, setOpen] = useState(false)   // collapsed by default — it's a summary, tap ▼ to expand
  const withTd = (games || []).filter(g => g.proj_tds != null)
    .slice().sort((a, b) => (b.proj_tds || 0) - (a.proj_tds || 0))
  if (!withTd.length) return null
  const total = withTd.reduce((s, g) => s + (g.proj_tds || 0), 0)
  const perGame = total / withTd.length
  return (
    <div style={{ background: `linear-gradient(90deg, ${T.greenDim}, ${T.card})`, border: `1px solid ${T.greenMid}`, borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, background: 'transparent', border: 'none', padding: '12px 14px', color: T.text, cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 19 }}>🏈</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>~{Math.round(total)} TDs projected · <span style={{ color: T.green }}>~{perGame.toFixed(1)}/game</span></div>
          <div style={{ fontSize: 10.5, color: T.muted, marginTop: 2 }}>Model expectation across {withTd.length} games — sum of every player's TD probability</div>
        </div>
        <span style={{ fontSize: 12, color: T.muted2 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: '6px 6px 8px' }}>
          {withTd.map(g => (
            <div key={g.game_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.away?.abbr} <span style={{ color: T.muted, fontWeight: 600 }}>@</span> {g.home?.abbr}</div>
                <div style={{ fontSize: 10, color: T.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.stadium || g.window || ''}{g.td_leader ? ` · led by ${g.td_leader}` : ''}</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, fontFamily: T.mono, flexShrink: 0, color: g.proj_tds >= 3 ? T.green : g.proj_tds <= 1.5 ? T.muted2 : T.text }}>🏈 {g.proj_tds.toFixed(1)}</div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: T.muted, padding: '6px 12px 2px', lineHeight: 1.4 }}>Expected TD scorers per game — the sum of every player's anytime-TD probability. Research only · 21+.</div>
        </div>
      )}
    </div>
  )
}

function Slate({ season, week, onGame, onMakeCard }) {
  const [games, setGames] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [meta, setMeta] = useState({})       // slate honesty stamps (injuries loaded / coverage)
  const [q, setQ] = useState('')
  const [win, setWin] = useState('All')      // time-slot filter
  const [mkt, setMkt] = useState({})         // game_id -> market movement (ML/spread/total)

  useEffect(() => {
    let alive = true; setLoading(true)
    getNFLSlate(season, week).then(res => {
      if (!alive) return
      setGames(res.games || []); setSample(!!res._sample)
      setMeta({ injuries_loaded: res.injuries_loaded, coverage_ok: res.coverage_ok,
                games_empty: res.games_empty, games_total: res.games_total })
      setLoading(false)
    })
    // Game-line movement — independent + fail-soft; the card just omits the MONEY row until it lands.
    getNFLMarket(season, week).then(res => {
      if (!alive) return
      const by = {}; (res.games || []).forEach(g => { by[g.game_id] = g }); setMkt(by)
    })
    return () => { alive = false }
  }, [season, week])

  // Time-slot chips — the windows that actually exist this week, in preferred order.
  const present = [...new Set((games || []).map(g => g.window).filter(Boolean))]
  const windows = WINDOW_ORDER.filter(w => present.includes(w))

  // Game order — finished games sink, the rest run chronologically by kickoff (Thu → MNF),
  // instead of the backend's grade order.
  const _kc = (a, b) => { const ka = a.kickoff || a.gameday || '', kb = b.kickoff || b.gameday || ''
    return ka === kb ? 0 : !ka ? 1 : !kb ? -1 : (ka < kb ? -1 : 1) }
  const winFiltered = (games || []).filter(g => win === 'All' || g.window === win)
  const shown = winFiltered
    .filter(searchMatch(q, g => [g.home?.abbr, g.home?.city, g.away?.abbr, g.away?.city]))
    .slice().sort((a, b) => ((a.state === 'final') - (b.state === 'final')) || _kc(a, b))

  return (
    <div>
      {sample && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8, letterSpacing: '0.05em' }}>● Live slate unavailable right now — check back shortly</div>}
      {/* Honesty stamps — never pass off an unvetted or thin slate as complete. */}
      {!loading && meta.injuries_loaded === false && (
        <div style={{ fontSize: 10.5, color: T.amber, background: T.amberDim || 'rgba(245,166,35,0.1)',
          border: `1px solid ${T.amber}55`, borderRadius: 8, padding: '6px 9px', marginBottom: 8, lineHeight: 1.4 }}>
          ⚠ Injury report hasn’t loaded for this week — a player ruled out may still show as active. Verify status before betting.
        </div>
      )}
      {!loading && meta.coverage_ok === false && meta.games_empty > 0 && (
        <div style={{ fontSize: 10.5, color: T.amber, background: T.amberDim || 'rgba(245,166,35,0.1)',
          border: `1px solid ${T.amber}55`, borderRadius: 8, padding: '6px 9px', marginBottom: 8, lineHeight: 1.4 }}>
          ⚠ Board may be incomplete — {meta.games_empty} of {meta.games_total} games have no projected plays yet. Check back as the slate fills in.
        </div>
      )}
      {!loading && winFiltered.length > 0 && <TdBoard games={winFiltered} />}
      {games?.length > 0 && <SearchBar value={q} onChange={setQ} placeholder="Search team or city…" />}
      {windows.length > 1 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, margin: '2px 0 12px', WebkitOverflowScrolling: 'touch' }}>
          {['All', ...windows].map(w => {
            const on = win === w
            return (
              <button key={w} onClick={() => setWin(w)} style={{
                cursor: 'pointer', fontSize: 11.5, fontWeight: 800, padding: '6px 12px', borderRadius: 8,
                whiteSpace: 'nowrap', border: `1px solid ${on ? T.green : T.border}`,
                background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{w}</button>
            )
          })}
        </div>
      )}
      {loading ? <Spinner />
        : !games?.length ? <Empty message="No games scheduled" />
        : !shown.length ? <Empty message={win !== 'All' ? `No ${win} games` : `No games match "${q}"`} />
        : shown.map(g => (
            <div key={g.game_id}>
              <GameCard game={g} onOpen={onGame} market={mkt[g.game_id]} />
              {onMakeCard && (
                <button onClick={() => onMakeCard(g)} style={{
                  width: '100%', marginTop: -3, marginBottom: 12, padding: '9px 0', fontSize: 12.5, fontWeight: 800,
                  color: T.green, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, cursor: 'pointer' }}>
                  🧪 Generate for this game
                </button>
              )}
            </div>
          ))}
    </div>
  )
}

// Player lookup — type any name, tap a result to open his card. Uses the /nfl/players
// search; opens the standard player modal via onPlayer so you get his full cross-market
// board and matchup without leaving the Games tab.
function PlayerSearch({ onPlayer }) {
  const [q, setQ] = useState('')
  const [res, setRes] = useState([])
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); setOpen(false); return }
    let alive = true
    const t = setTimeout(() => {
      searchNFLPlayers(q.trim()).then(r => { if (alive) { setRes(r.players || []); setOpen(true) } })
    }, 200)
    return () => { alive = false; clearTimeout(t) }
  }, [q])
  return (
    <div style={{ position: 'relative', marginBottom: 12 }}>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Look up a player…"
        onFocus={() => res.length && setOpen(true)}
        style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 10,
          border: `1px solid ${T.border}`, background: T.card, color: T.text, fontSize: 14, outline: 'none' }} />
      {open && res.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 4,
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden',
          maxHeight: 300, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
          {res.map(p => (
            <button key={p.gsis_id} onClick={() => {
                onPlayer && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, pos: p.pos, team: p.team, status: 'active' })
                setOpen(false); setQ('')
              }}
              style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', textAlign: 'left', cursor: 'pointer',
                background: 'none', border: 'none', borderTop: `1px solid ${T.border}`, padding: '10px 13px', color: T.text }}>
              <span style={{ fontWeight: 700, fontSize: 13.5 }}>{p.name}</span>
              <span style={{ color: T.muted, fontSize: 11.5 }}>{p.pos}{p.team ? ` · ${p.team}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function GamesTab({ season, week, onGame, onPlayer, onMakeCard, pro, attackInitial, onAttackConsumed }) {
  const [mode, setMode] = useState('slate')   // open on the games list — the tab says "Games"
  const [attackSub, setAttackSub] = useState(null)
  // A My Board shortcut can deep-link straight into Attack → a sub (e.g. Defense breakdown).
  useEffect(() => { if (attackInitial) { setMode('attack'); setAttackSub(attackInitial); onAttackConsumed && onAttackConsumed() } }, [attackInitial])

  return (
    <div style={{ padding: '12px 16px 80px' }}>
      {onPlayer && <PlayerSearch onPlayer={onPlayer} />}
      <div style={{ display: 'flex', gap: 5, marginBottom: 12 }}>
        {MODES.map(([id, label]) => {
          const on = id === mode
          return (
            <button key={id} onClick={() => setMode(id)} style={{
              flex: 1, padding: '9px 1px', borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 11,
              border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
              color: on ? T.green : T.muted, whiteSpace: 'nowrap',
            }}>{label}</button>
          )
        })}
      </div>
      {mode === 'slate' && <Slate season={season} week={week} onGame={onGame} onMakeCard={onMakeCard} />}
      {mode === 'attack' && <AttackView key={attackSub || 'sheet'} initialSub={attackSub} season={season} week={week} onGame={onGame} onPlayer={onPlayer} pro={pro} />}
      {mode === 'top' && <TopPlays season={season} week={week} />}
    </div>
  )
}
